// @ts-expect-error - TS2614 - Module '"src/api-parsers/index"' has no exported member 'AgencyOverview'. Did you mean to use 'import AgencyOverview from "src/api-parsers/index"' instead?
import type {AgencyOverview} from 'src/api-parsers/index';
import type {PaginatedResponses} from 'src/types/report';
import * as React from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useHistory} from 'src/rerouter';
import moment from 'moment';
import {Helmet} from 'react-helmet';
import {useTrackedAction} from 'src/hooks/useTracking';
import useI18n from 'src/hooks/useI18n';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {usePageTitle} from 'src/contexts/page-title-context';
import {selectEntityBySenseName} from 'src/selectors/ats-entities';
import {selectCorporateLabels} from 'src/selectors/language-support';
import {isInternalUser} from 'src/utils/check-internal-user';
import {selectReleaseFlags} from 'src/selectors/product-flags';
import {AnalyticsService} from 'src/analytics';
import {PRODUCT_TRACKING_EVENTS} from 'src/constants/product-tracking-events';
import {getCurrentAgent} from 'src/selectors/accounts';
import {selectAgencyConfig, selectAgencyData} from 'src/selectors/agency';
import {SURVEY_SUMMARY} from 'src/components/sense-ai-panel/tracking-events';
import {hasPrivilege} from 'src/utils/accounts';
import Tile from 'src/components/lib/tile';
import UserSearch from 'src/components/lib/user-token-search';
import DashboardRoi from 'src/components/roi/dashboard-roi';
import DashboardResponses from './dashboard-responses';
import {TitleMedium} from '@spaced-out/ui-design-system/lib/components/Text';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {PageName} from '@spaced-out/ui-design-system/lib/components/PageTitle';
import EventsTable from './recent-events';
import OnboardPageNavigator from 'src/components/agency-onboard/onboard-page-navigator/onboard-page-navigator';
import PeopleIcon from 'src/images/nav/people-icon.svg';
import ScheduleIcon from 'src/images/nav/schedule-icon.svg';
import WorkflowIcon from 'src/images/nav/workflow-icon.svg';
import NpsIcon from 'src/images/event-types/nps.svg';
import useAgencyConfig, {useShowJourneyButton} from 'src/hooks/useAgencyConfig';
import {useAudienceFilterSeparation} from 'src/hooks/product-flags';
import {setUrlSuffixAndOpenPanel} from 'src/action-creators/sense-ai-panel';
import {
  USE_SURVEY_SUMMARY_GENERATOR,
  VIEW_WORKFLOWS,
} from 'src/action-creators/roles';
import {duotoneIconStyle} from 'src/utils/app-shell';
import css from './dashboard.css';
import {useJourneysEnabled} from 'src/hooks/useDisplayConfig';


export default function Dashboard({
  agencyOverview,
  responseData,
  flaggedResponseData,
  name,
  flaggedWords,
  multiEntityEnabled,
}: {
  agencyOverview: AgencyOverview;
  responseData: PaginatedResponses;
  flaggedResponseData: PaginatedResponses;
  name: string;
  flaggedWords: string[];
  multiEntityEnabled: boolean;
}): React.ReactElement<React.ComponentProps<'div'>> {
  const {
    sentEvents,
    queuedEvents,
    totalCandidates,
    activeAudienceMembers,
    nps,
    recentlyVisitedEvents,
  } = agencyOverview;

  const {
    actions: {setPageTitleProps},
  } = usePageTitle();
  const {labelI18nInstance} = useI18n();
  const {t, makeKey} = useLabelTranslation();

  const language = labelI18nInstance.language;
  const router = useHistory();
  const dispatch = useDispatch();
  const state = useSelector((state) => state);
  const {id: agencyId, name: agencyName} = useSelector(selectAgencyData);
  // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'Account | AuthedUserAccount | null | undefined'.
  const {id: agentId} = useSelector((state) => getCurrentAgent(state));
  const journeysEnabled =
    useJourneysEnabled() && hasPrivilege(state, VIEW_WORKFLOWS);
  // NOTE (kyle): impurity
  const date = moment();

  // NOTE (kyle): this seems like a common pattern?
  const entityType = useSelector(
    (state) => selectEntityBySenseName(state, 'candidate')?.name,
  );

  const roiV2Enabled = useSelector(selectReleaseFlags).roiV2Enabled;
  const corporateLabels = useSelector(selectCorporateLabels);
  const isJourneyV2 = useAudienceFilterSeparation();
  const showJourneyButton = useShowJourneyButton();
  const customerBusinessType =
    useSelector(selectAgencyConfig).customerBusinessType;
  const isCorporate = customerBusinessType === 'corporate';
  const isViewSummaryVisible = hasPrivilege(
    state,
    USE_SURVEY_SUMMARY_GENERATOR,
  );

  const handleAnalyticsEvents = () => {
    try {
      const {accounts, agency, productFlags} = state;
      const userId = accounts?.authedUserId;
      const data = {
        // @ts-expect-error - TS2339 - Property 'data' does not exist on type 'DefaultRootState'. | TS2339 - Property 'agency' does not exist on type 'DefaultRootState'. | TS2339 - Property 'productFlags' does not exist on type 'DefaultRootState'.
        roles: accounts.data[userId].securityRole.privileges,
        release_flags: productFlags.releaseFlags,
        agencyName: agency.agency.name,
        agency_id: agency.agency.id,
        // @ts-expect-error - TS2339 - Property 'agency' does not exist on type 'DefaultRootState'.
        ats_kind: agency.agency.atsSystem,
      } as const;
      // @ts-expect-error - TS2339 - Property 'authedUserId' does not exist on type 'DefaultRootState'.
      AnalyticsService.identify(userId, {...data});
      // @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
      AnalyticsService.track(PRODUCT_TRACKING_EVENTS.DASHBOARD);
    } catch (error: any) {}
  };

  const viewSurveySummaries = () => {
    dispatch(
      setUrlSuffixAndOpenPanel(
        `/survey-summary/saved-summaries?source=dashboard`,
      ),
    );
  };

  const trackedViewSummaryClick = useTrackedAction(
    SURVEY_SUMMARY.SURVEY_SUMMARY_VISITED,
    viewSurveySummaries,
    {
      agentId,
      agencyId,
      agencyName,
      internalUser: isInternalUser(),
      pageName: 'Admin Landing Page (Dashboard)',
      pageUrl: router.location.pathname,
    },
  );

  React.useEffect(() => {
    handleAnalyticsEvents();
  }, []);

  React.useEffect(() => {
    setPageTitleProps({
      customPageNameComponent: (
        <PageName>
          <TitleMedium>{t('Dashboard', 'Dashboard')}</TitleMedium>
          <Icon type="duotone" name="house" style={duotoneIconStyle} />
        </PageName>
      ),
      showBackButton: false,
    });
  }, [language]);

  return (
    <div className={css.bg}>
      {/* @ts-expect-error - TS2786 - 'Helmet' cannot be used as a JSX component. */}
      <Helmet title={t('Dashboard', 'Dashboard')} />
      <div className={css.container} data-it-dashboard data-qa-id="dashboard">
        {useAgencyConfig('onboardRequired') ? <OnboardPageNavigator /> : <></>}
        {roiV2Enabled ? (
          <>
            {!isCorporate && (
              <div
                className={css.dashboardSection}
                data-qa-id="dashboard-roi-section"
              >
                <div className={css.subtitle}>
                  {t(
                    'Return_on_investment_in_the_previous_quarter',
                    'Return on investment in the previous quarter',
                  )}
                </div>
                <DashboardRoi />
              </div>
            )}
          </>
        ) : (
          <>
            <div className={css.header} data-qa-id="dashboard-header-section">
              <h1 className={css.title}>{`${t(
                'Welcome',
                'Welcome',
              )}, ${name}`}</h1>
              <div>
                {/* @ts-expect-error - TS2739 - Type '{ className: string; entityType: EntityType; onSearchResultClick: (audienceMemberID: string) => Promise<void>; placeholder: string; isPersonEntity: true; }' is missing the following properties from type 'TypeaheadProps<AudienceEntity | { id: string; email?: string | undefined; emailaddress: string; }>': searchResults, clearSearchResults, onSearch */}
                <UserSearch
                  className={css.userSearch}
                  entityType={entityType}
                  onSearchResultClick={(audienceMemberID) =>
                    router.push(`/people/candidates/${audienceMemberID}`)
                  }
                  placeholder={t('Search_for_a_person', 'Search for a person')}
                  isPersonEntity
                />
                <span className={css.date}>
                  <span className={css.month}>{date.format('MMMM')} </span>
                  <span className={css.day}>{date.format('D')}</span>
                </span>
              </div>
            </div>

            <div className={css.stats} data-qa-id="dashboard-tile-section">
              <Tile
                number={sentEvents}
                label={t(
                  'Touchpoints_in_Last_7_Days',
                  'Touchpoints in Last 7 Days',
                )}
                icon={<WorkflowIcon className={css.statsWorkflowIcon} />}
                to="/schedule"
              />

              <Tile
                number={queuedEvents}
                label={t('Upcoming_Touchpoints', 'Upcoming Touchpoints')}
                icon={<ScheduleIcon className={css.statsScheduleIcon} />}
                to="/schedule"
              />

              {multiEntityEnabled ? (
                <Tile
                  number={totalCandidates}
                  label={`${t('Total', 'Total')} ${t(
                    makeKey(corporateLabels['sense_candidates']),
                    'Candidates',
                  )}`}
                  icon={<PeopleIcon className={css.statsPeopleIcon} />}
                  to="/people"
                />
              ) : (
                <Tile
                  number={activeAudienceMembers || '-'}
                  label={t('Active_People', 'Active People')}
                  icon={<PeopleIcon className={css.statsPeopleIcon} />}
                  to="/people"
                />
              )}

              {nps && (
                <Tile
                  number={nps.score.toLocaleString(undefined, {
                    maximumFractionDigits: 1,
                  })}
                  label={t('NPS', 'NPS')}
                  icon={<NpsIcon className={css.statsWorkflowIcon} />}
                />
              )}
            </div>
          </>
        )}

        {journeysEnabled && (
          <div
            className={css.dashboardSection}
            data-qa-id="dashboard-events-section"
          >
            <div className={css.subtitle}>
              {t('Recently_Viewed', 'Recently Viewed')}
            </div>
            <EventsTable fetching={!recentlyVisitedEvents} />
          </div>
        )}
        <DashboardResponses
          recentResponseData={responseData}
          flaggedResponseData={flaggedResponseData}
          flaggedWords={flaggedWords}
          multiEntityEnabled={multiEntityEnabled}
          isJourneyV2={isJourneyV2}
          showJourneyButton={showJourneyButton}
          handleViewSummaryClick={trackedViewSummaryClick}
          isViewSummaryVisible={isViewSummaryVisible}
        />
      </div>
    </div>
  );
}
